<template>
  <div class="min-h-screen flex flex-col">
    <!-- SEO Meta Tags -->
    <Head>
      <title v-if="title">{{ title }} - RealGestión</title>
      <meta name="description" :content="description || 'RealGestión - La solución integral para la gestión de tu negocio. Administra clientes, productos, inventarios, compras y ventas de forma eficiente.'" />
      <meta name="keywords" content="gestión de negocios, control de inventario, gestión de clientes, sistema de ventas, facturación, administración de negocios" />
      
      <!-- Open Graph / Facebook -->
      <meta property="og:type" content="website" />
      <meta property="og:url" :content="url || 'https://realgestion.com'" />
      <meta property="og:title" :content="title || 'RealGestión - Gestiona tu negocio de forma eficiente'" />
      <meta property="og:description" :content="description || 'La solución integral para la gestión de tu negocio. Administra clientes, productos, inventarios, compras y ventas de forma eficiente.'" />
      <meta property="og:image" :content="image || 'https://realgestion.com/images/og-image.jpg'" />

      <!-- Twitter -->
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" :content="url || 'https://realgestion.com'" />
      <meta property="twitter:title" :content="title || 'RealGestión - Gestiona tu negocio de forma eficiente'" />
      <meta property="twitter:description" :content="description || 'La solución integral para la gestión de tu negocio. Administra clientes, productos, inventarios, compras y ventas de forma eficiente.'" />
      <meta property="twitter:image" :content="image || 'https://realgestion.com/images/og-image.jpg'" />

      <!-- Canonical URL -->
      <link rel="canonical" :href="url || 'https://realgestion.com'" />

      <!-- Favicon -->
      <link rel="icon" type="image/png" href="/favicon.png" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />

      <!-- Additional Meta Tags -->
      <meta name="robots" content="index, follow" />
      <meta name="language" content="Spanish" />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="RealGestión" />
    </Head>

    <!-- Header -->
    <header class="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
      <nav class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex">
            <div class="flex-shrink-0 flex items-center">
              <Link href="/" class="text-2xl font-bold text-black">
                RealGestión
              </Link>
            </div>
            <div class="hidden sm:ml-6 sm:flex sm:space-x-8">
              <Link href="/#features" class="border-transparent text-gray-500 hover:border-teal-500 hover:text-teal-600 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                Funcionalidades
              </Link>
              <Link href="/#pricing" class="border-transparent text-gray-500 hover:border-teal-500 hover:text-teal-600 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                Precios
              </Link>
              <Link href="/#testimonials" class="border-transparent text-gray-500 hover:border-teal-500 hover:text-teal-600 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                Testimonios
              </Link>
              <Link href="/about" class="border-transparent text-gray-500 hover:border-teal-500 hover:text-teal-600 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                Sobre Nosotros
              </Link>
            </div>
          </div>
          <div class="hidden sm:ml-6 sm:flex sm:items-center">
            <Link href="/login" class="text-gray-500 hover:text-teal-600 px-3 py-2 rounded-md text-sm font-medium">
              Iniciar sesión
            </Link>
            <Link href="/register" class="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700">
              Registrarse
            </Link>
          </div>
        </div>
      </nav>
    </header>

    <!-- Main Content -->
    <main class="flex-grow pt-16">
      <slot></slot>
    </main>

    <!-- Footer -->
    <footer class="bg-gray-800">
      <div class="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div class="col-span-1 md:col-span-2">
            <Link href="/" class="text-2xl font-bold text-white">
              Realgestión
            </Link>
            <p class="mt-4 text-gray-300">
              La solución integral para la gestión de tu negocio. Administra clientes, productos, inventarios, compras y ventas de forma eficiente.
            </p>
          </div>
          <div>
            <h3 class="text-sm font-semibold text-gray-400 tracking-wider uppercase">
              Producto
            </h3>
            <ul class="mt-4 space-y-4">
              <li>
                <Link href="#features" class="text-base text-gray-300 hover:text-white">
                  Funcionalidades
                </Link>
              </li>
              <li>
                <Link href="#pricing" class="text-base text-gray-300 hover:text-white">
                  Precios
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h3 class="text-sm font-semibold text-gray-400 tracking-wider uppercase">
              Legal
            </h3>
            <ul class="mt-4 space-y-4">
              <li>
                <Link href="/terms" class="text-base text-gray-300 hover:text-white">
                  Términos
                </Link>
              </li>
              <li>
                <Link href="/privacy" class="text-base text-gray-300 hover:text-white">
                  Privacidad
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div class="mt-8 border-t border-gray-700 pt-8 md:flex md:items-center md:justify-between">
          <div class="flex space-x-6 md:order-2">
            <a target="_blank" href="https://www.instagram.com/realgestion" class="text-gray-400 hover:text-gray-300">
              <span class="sr-only">Instagram</span>
              <i class="fab fa-instagram text-xl"></i>
            </a>
          </div>
          <p class="mt-8 text-base text-gray-400 md:mt-0 md:order-1">
            &copy; {{ new Date().getFullYear() }} Realgestión. Todos los derechos reservados.
            <span class="block mt-1">
              Desarrollado por <a href="https://conexioncreativa.net" target="_blank" class="text-teal-400 hover:text-teal-300">ConexionCreativa</a>
            </span>
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { Link, Head } from '@inertiajs/inertia-vue3'

export default {
  components: {
    Link,
    Head
  },
  props: {
    title: {
      type: String,
      default: null
    },
    description: {
      type: String,
      default: null
    },
    url: {
      type: String,
      default: null
    },
    image: {
      type: String,
      default: null
    }
  }
}
</script> 